/**
 * Fetches the catalog of chains + tokens authorised by the current API key.
 * Discriminated-union state lets the caller render loading / error / data
 * without optional chaining noise.
 *
 * Triggers a refetch whenever endpoint or apiKey change. In-flight requests
 * are cancelled (results discarded) so a key swap mid-flight cannot land a
 * stale catalog from the previous tenant.
 *
 * The two queries run in parallel — they are independent and the worker
 * caches them with SWR, so the wait is at most one round-trip.
 *
 * Both queries are sent via APQ — gqlFetch handles the sha256 + extensions
 * envelope internally.
 */

import { useEffect, useState } from "react";

import type { ChainDef } from "../data/tokens.js";

import { CHAINS_QUERY, TOKENS_QUERY, gqlFetch } from "../lib/graphql.js";
import {
  mergeCatalog,
  type ChainsResponseData,
  type TokensResponseData,
} from "../lib/mergeCatalog.js";

export type CatalogState =
  | { kind: "idle" } // endpoint or apiKey empty — nothing to fetch
  | { kind: "loading" }
  | { kind: "ready"; chains: readonly ChainDef[] }
  | { kind: "error"; message: string };

export function useCatalog(endpoint: string, apiKey: string): CatalogState {
  const [state, setState] = useState<CatalogState>({ kind: "idle" });

  useEffect(() => {
    if (endpoint === "" || apiKey === "") {
      // Legitimate: state is derived from props (endpoint/apiKey). When they
      // go empty, the effect must reset to "idle" — there is no external
      // system to listen on, so setState IS the only way to communicate.
      // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect
      setState({ kind: "idle" });
      return;
    }

    let cancelled = false;
    // Legitimate: marks the start of the async fetch lifecycle. Switching to
    // useReducer would not change the semantics, only the call site.
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setState({ kind: "loading" });

    (async () => {
      try {
        const [chainsRes, tokensRes] = await Promise.all([
          gqlFetch<ChainsResponseData>({
            endpoint,
            apiKey,
            query: CHAINS_QUERY,
            operationName: "Chains",
          }),
          gqlFetch<TokensResponseData>({
            endpoint,
            apiKey,
            query: TOKENS_QUERY,
            operationName: "Tokens",
          }),
        ]);

        if (cancelled) return;

        // Surface GraphQL-level errors (auth, validation, etc.)
        const errs = [
          ...(chainsRes.response.errors ?? []),
          ...(tokensRes.response.errors ?? []),
        ];
        if (errs.length > 0) {
          setState({
            kind: "error",
            message: errs.map((e) => e.message).join("; "),
          });
          return;
        }

        const merged = mergeCatalog(
          chainsRes.response.data,
          tokensRes.response.data,
        );
        setState({ kind: "ready", chains: merged });
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: "error",
          message: err instanceof Error ? err.message : "Unknown catalog error",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [endpoint, apiKey]);

  return state;
}
